import { ApolloError } from '@apollo/client';
import React, { FC, useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import { useAuth } from 'react-oidc-context';
import DOMPurify from 'dompurify';
import { APP_URLS, locale } from '../../../consts';
import {
  MagicxDataGetBySlugQuery,
  useMagicxDataGetBySlugLazyQuery,
} from '../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/get-magicx-data-by-slug.query.generated';
import {
  activePromoStatuses,
  availablePromoStatuses,
  ParsedPromo,
  PromoTypes,
  RewardPromo,
} from '../../Layout/Promotions/types';
import { MediaBreakpoint, useBreakpointInfo } from '../../../hooks/useDeviceInfo';
import { getImageUrl } from '../../../utils/navigation';
import { isProgressBar, showCurrency } from '../../Layout/Promotions/utils';
import { usePromoLoggedOut } from '../../../hooks/usePromoLoggedOut';
import { mockWage, mockWageRequired } from '../../Layout/Promotions/consts';
import { Endpoint } from '../../../models/api.model';
import { PromotionError } from '../../Layout/Promotions/PromotionError';
import { Spinner } from '../../shared/Spinner';
import { Button } from '../../shared/Button';
import { getDaysLeft } from '../../../utils/dates';
import { ProgressBar } from '../../shared/ProgressBar';
import { CardCTASection } from '../../Layout/Promotions/PromotionCard/CardCTASection';
import { RecommendedGameCarousel } from '../../Games';
import { PromotionGames } from '../../Games/games.model';
import { PromotionClaim } from '../../Layout/Promotions/PromotionClaimPrompt';
import { useFetchPlatformPromos } from '../../../hooks/useFetchPlatformPromos';
import './ItvPromo.scss';
import { evaluatePromosWithPlatform, isAnyPlatformPromoReceived } from '../ItvRowCollection/utils/platformPromos';

export const ItvPromo: FC = () => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const location = useLocation();
  const platformPromos = useFetchPlatformPromos();
  const isUserAuthenticated = useAuth()?.isAuthenticated;
  const sanitizer = DOMPurify.sanitize;
  const { pathname: pageUrl, hash } = location;

  const isTermsPage = hash === APP_URLS.terms;
  const promotionSlug = String(useParams().id);

  const [directusMagicXPromotion, setDirectusMagicXPromotion] = useState<MagicxDataGetBySlugQuery | undefined>();
  const [resultPromotion, setResultPromotion] = useState<ParsedPromo | null>(null);
  const [showClaim, setShowClaim] = useState<boolean>(false);

  const isTablet = useBreakpointInfo([MediaBreakpoint.TABLET]);
  const isMobile = useBreakpointInfo([MediaBreakpoint.MOBILE]);

  const isActive = activePromoStatuses.includes(String(resultPromotion?.status));
  const isAvailable = availablePromoStatuses.includes(String(resultPromotion?.status));

  const promotion = directusMagicXPromotion?.magicx_promotions[0];
  const reward = directusMagicXPromotion?.magicx_rewards[0];
  const { isPromosLoading, promosLoadingError } = platformPromos;

  const {
    id,
    bonus_ID: bonusId,
    magicx_slug: magicXSlug,
    start_date: startDate,
    start_time: startTime,
    end_date: endDate,
    end_time: endTime,
    translations,
    eligible_games: eligibleGames,
    DM_Template_ID: magicDmBonusId,
    DM_Max_Amount: magicDmMaxAmount,
    bonus_type: promoType,
    thumbnail,
    thumb_expanded_desktop,
    thumb_expanded_mobile,
    reward_ID: rewardId,
    reward_type: rewardType,
  } = (promotion as typeof promotion & RewardPromo) || (reward as typeof promotion & RewardPromo) || {};

  const resTransl = translations?.[0];
  const title = resTransl?.title || '';
  const subtitle = resTransl?.subtitle || '';
  const description = resTransl?.description || '';
  const imgPath = getImageUrl(
    isMobile || isTablet ? thumb_expanded_mobile?.filename_disk : thumb_expanded_desktop?.filename_disk
  );
  const imgAlt = thumbnail?.title || '';
  const terms = resTransl?.terms;
  const howToClaim = resTransl?.how_to_claim;
  const eligibleCustomers = resTransl?.eligible_customers;
  const parsedPromo = {
    id,
    bonusId,
    rewardId,
    magicXSlug,
    startDate: startDate && startTime ? `${startDate}T${startTime}` : startDate || '',
    endDate: endDate && endTime ? `${endDate}T${endTime}` : endDate || '',
    title,
    description,
    imgPath,
    imgAlt,
    pageUrl,
    hideTerms: isTermsPage,
    dmMaxAmount: magicDmMaxAmount,
    dmBonusId: magicDmBonusId,
    promoType: promoType || rewardType,
    ctaCopy: translations?.[0]?.cta_copy,
    ctaUrl: translations?.[0]?.cta_url,
  };

  const STEPS_DATA = [
    t('promotions.details.steps.first'),
    t('promotions.details.steps.second'),
    t('promotions.details.steps.third'),
  ];
  const SUMMARY_DATA_TITLES = [
    t('promotions.details.summary.eligible-customers'),
    t('promotions.details.summary.how-to-claim'),
  ];

  const showCTA = isUserAuthenticated ? isActive || isAvailable : promoType !== PromoTypes.NETWORK;

  const fullPromo = { ...parsedPromo, ...resultPromotion };

  const showProgressBar = isProgressBar(resultPromotion?.status, parsedPromo.promoType);

  const { storedPromo } = usePromoLoggedOut(fullPromo, showClaim);

  const wageNumber = Number(resultPromotion?.wageredAmount ?? mockWage);
  const wageReqNumber = Number(resultPromotion?.wageringRequirementAmount ?? mockWageRequired);

  const [fetchMagicXDetailsBySlug, { error: fetchMagicXDetailsBySlugError, loading: promoDetailsLoading }] =
    useMagicxDataGetBySlugLazyQuery({
      variables: {
        language: locale,
        slugs: [promotionSlug],
      },
      onCompleted: setDirectusMagicXPromotion,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      onError: (e: ApolloError): void => console.error(e.message),
      context: { endpoint: Endpoint.betzoneDirectusAPI },
    });

  useEffect(() => {
    (async (): Promise<void> => {
      await fetchMagicXDetailsBySlug();
    })();
  }, []);

  useEffect(() => {
    if (!isPromosLoading) {
      const isPlatformPromoReceived = isAnyPlatformPromoReceived(platformPromos);

      if (isPlatformPromoReceived) {
        const compiledPromo = evaluatePromosWithPlatform(parsedPromo, platformPromos);

        compiledPromo && setResultPromotion(compiledPromo);
      }
    }
  }, [isPromosLoading]);

  useEffect(() => {
    storedPromo && setShowClaim(true);
  }, [storedPromo]);

  const isPromoLoading = promoDetailsLoading || isPromosLoading;
  const showError = isPromoLoading && !directusMagicXPromotion;
  const loadingError = promosLoadingError || fetchMagicXDetailsBySlugError;

  return (
    <div className="promotion-main-wrapper">
      {isPromoLoading ? (
        <Spinner />
      ) : showError ? (
        <PromotionError type={loadingError ? 'loading' : 'not-found'} />
      ) : (
        <>
          <div className="promotion-main-wrapper__back-button">
            <Button
              type="button"
              variant="secondary-alt"
              style={{ width: '8rem', height: '2.5rem' }}
              onClick={(): void => navigate(-1)}
            >
              {t('promotions.button.back.text')}
            </Button>
          </div>

          <div className="promotion-main-wrapper__content">
            <div
              className="promotion-main-wrapper__promo-image"
              style={{
                backgroundImage: `url(${imgPath})`,
              }}
            />

            <div className="promotion-main-wrapper__details-section">
              <div className="promotion-main-wrapper__details-content">
                <h1 className="promotion-main-wrapper__details-title">{title}</h1>
                {subtitle && <h1 className="promotion-main-wrapper__details-title">{subtitle}</h1>}

                {endDate && (
                  <div className="promotion-main-wrapper__status">
                    <span className="promotion-main-wrapper__time-left">
                      {getDaysLeft(endDate && endTime ? `${endDate}T${endTime}` : endDate)}
                    </span>
                  </div>
                )}

                <div className="promotion-main-wrapper__disclaimers">
                  <p dangerouslySetInnerHTML={{ __html: sanitizer(description) }} />
                </div>
                <div className="promotion-main-wrapper__steps">
                  <h3>{t('promotions.details.steps.title')}</h3>
                  <div className="promotion-main-wrapper__steps-progress">
                    {STEPS_DATA.map((step, index) => (
                      <React.Fragment key={step}>
                        <div className="promotion-main-wrapper__step">
                          <div className="promotion-main-wrapper__step-number">{++index}</div>
                          <span className="promotion-main-wrapper__step-text">{step}</span>
                        </div>
                        {index < STEPS_DATA.length && (
                          <svg
                            className="promotion-main-wrapper__step-dash"
                            viewBox="0 0 2 2"
                            preserveAspectRatio="none"
                            aria-hidden="true"
                          >
                            <line x1="0" y1="1" x2="100" y2="1" className="promotion-main-wrapper__step-dash-line" />
                          </svg>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                {showProgressBar && (
                  <ProgressBar
                    showWageringInfo
                    showCurrency={showCurrency(resultPromotion?.type)}
                    value={wageNumber}
                    min={0}
                    max={wageReqNumber}
                    type="success"
                    className="promotion-main-wrapper__progress-bar"
                  />
                )}
                {showCTA && (
                  <CardCTASection
                    onClaimClick={(): void => setShowClaim(true)}
                    showSecondary={false}
                    isActivePromotions={isActive}
                    promo={parsedPromo}
                  />
                )}

                {isUserAuthenticated && (
                  <RecommendedGameCarousel
                    title={t('promotions.details.eligible-games.title')}
                    eligibleGames={eligibleGames as PromotionGames}
                    showArrows={true}
                  />
                )}
                {terms && (
                  <div className="promotion-main-wrapper__terms">
                    <h3>{t('promotions.details.terms.title')}</h3>
                    <div className="promotion-main-wrapper__terms-content">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: sanitizer(terms),
                        }}
                      />
                    </div>
                  </div>
                )}

                <div className="promotion-main-wrapper__summary">
                  {[eligibleCustomers, howToClaim].map(
                    (item, index) =>
                      item && (
                        <div key={index} className="promotion-main-wrapper__summary-item">
                          <h4 className="promotion-main-wrapper__summary-title">{SUMMARY_DATA_TITLES[index]}</h4>
                          <p
                            className="promotion-main-wrapper__summary-text"
                            dangerouslySetInnerHTML={{
                              __html: sanitizer(item),
                            }}
                          />
                        </div>
                      )
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {showClaim && <PromotionClaim promo={storedPromo || fullPromo} setShowClaim={setShowClaim} />}
    </div>
  );
};

export default ItvPromo;
